/* eslint max-lines: ["error", {"max": 400, "skipComments": true}] */

import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import * as actionTypes from '../action-types';
import { getUpdatedStateFromAdvice } from '../utils';
import {
  aggregationSeparator,
  defaultPaginationSize,
  defaultSearchType,
  defaultSearchSort,
} from '../utils/constants';
import { QueryParamsType } from './queryParams.types';
import { SearchActions, QueryParamsActions } from '../actions';

export const defaultState: QueryParamsType = {
  userQuery: '',
  type: defaultSearchType,
  sort: defaultSearchSort,
  aggregationsPaths: [],
  excludeMoyensAnnexes: false,
  from: 0,
  size: defaultPaginationSize,
  dates: {},
  parties: {},
  onlyTopResults: true,
  selectedKeywords: [],
  restrictToLawArticleIds: [],
  shouldResetParams: false,
  lastSearchQuery: '',
};

function getFreshQueryParams() {
  const { from, size, originalQueryKey } = defaultState;
  return {
    from,
    size,
    originalQueryKey,
  };
}

export function queryParamsReducer(
  state: QueryParamsType = cloneDeep(defaultState),
  action: SearchActions | QueryParamsActions,
): QueryParamsType {
  switch (action.type) {
    case actionTypes.SYNC_QUERY_RESPONSE: {
      const { response, queryParams } = action.payload;

      const nextState = {
        ...state,
        lastSearchQuery: queryParams.userQuery,
        shouldResetParams: false,
        aggregationsPaths: [...state.aggregationsPaths],
        from: response.hits.length,
        size: defaultPaginationSize,
        originalQueryKey: state.originalQueryKey || response.key, // OQK can be preset in the url (ex: back from decision)
      };

      return nextState;
    }
    case actionTypes.SYNC_PAGINATION_RESPONSE: {
      const { response } = action.payload;
      return {
        ...state,
        from: state.from + response.hits.length,
        size: defaultPaginationSize,
      };
    }
    case actionTypes.SEARCH_REQUEST: {
      const { restrictedDocIds } = action.payload;

      if (state.shouldResetParams) {
        return {
          ...cloneDeep(defaultState),
          shouldResetParams: false,
          userQuery:
            state.userQuery.length > 0
              ? state.userQuery
              : state.lastSearchQuery,
          lastSearchQuery: '',
          type: state.type,
          ...(restrictedDocIds && {
            restrictToLawArticleIds: restrictedDocIds,
          }),
        };
      }
      return state;
    }

    case actionTypes.USER_TYPE: {
      const { query } = action.payload;

      const newState = { ...state, userQuery: query };
      // user selects all the query and start typing another one
      const isUserReplacingTheQuery =
        query.length === 1 && state.userQuery.length > 2;
      if (!query || isUserReplacingTheQuery) {
        newState.shouldResetParams = true;
      }

      return newState;
    }

    case actionTypes.UPDATE_QUERY: {
      const { query } = action.payload;
      const newState = { ...state, ...getFreshQueryParams(), userQuery: query };

      if (!query) {
        newState.shouldResetParams = true;
      }
      return newState;
    }

    case actionTypes.UPDATE_SORT: {
      const { sort } = action.payload;
      return {
        ...state,
        ...getFreshQueryParams(),
        sort,
      };
    }

    case actionTypes.TOGGLE_AGGREGATION: {
      const { aggregationPath, value } = action.payload;
      let newPaths;

      if (value) {
        const [domain, ...subs] = aggregationPath;

        const allPaths = subs.reduce<Array<string>>((acc, key, index) => {
          const prefix = index === 0 ? domain : acc[index - 1];
          acc.push(`${prefix}${aggregationSeparator}${key}`);
          return acc;
        }, []);
        const previousSet = new Set(state.aggregationsPaths);
        allPaths.forEach((item) => previousSet.add(item));
        newPaths = Array.from(previousSet);
      } else {
        const aggregationPathFormated = aggregationPath.join(
          aggregationSeparator,
        );
        newPaths = state.aggregationsPaths.filter(
          (path) =>
            path !== aggregationPathFormated &&
            path.indexOf(
              `${aggregationPathFormated}${aggregationSeparator}`,
            ) === -1,
        );
      }

      return {
        ...state,
        ...getFreshQueryParams(),
        aggregationsPaths: newPaths,
      };
    }

    case actionTypes.TOGGLE_EXCLUDE_MOYENS_ANNEXES: {
      return {
        ...state,
        ...getFreshQueryParams(),
        excludeMoyensAnnexes: !state.excludeMoyensAnnexes,
      };
    }

    case actionTypes.UPDATE_DATES: {
      return {
        ...state,
        ...getFreshQueryParams(),
        dates: action.payload.dates,
      };
    }

    case actionTypes.UPDATE_PARTIES: {
      return {
        ...state,
        ...getFreshQueryParams(),
        parties: action.payload.parties,
      };
    }
    case actionTypes.UPDATE_LAWYER_FILTER: {
      return {
        ...state,
        ...getFreshQueryParams(),
        lid: action.payload.lawyerId,
      };
    }

    case actionTypes.UPDATE_ENTERPRISE_FILTER: {
      return {
        ...state,
        ...getFreshQueryParams(),
        eid: action.payload.enterpriseId,
      };
    }

    case actionTypes.UPDATE_LAW_OFFICE_FILTER: {
      return {
        ...state,
        ...getFreshQueryParams(),
        oid: action.payload.lawOfficeId,
      };
    }

    case actionTypes.UPDATE_TYPE: {
      const { userQuery } = state;
      return {
        ...cloneDeep(defaultState),
        type: action.payload.type,
        userQuery,
      };
    }

    case actionTypes.FOLLOW_ADVICE: {
      return getUpdatedStateFromAdvice(state, action.payload.advice);
    }

    case actionTypes.FORCE_TYPO: {
      return {
        ...state,
        ...getFreshQueryParams(),
        disableAutocorrect: true,
      };
    }

    case actionTypes.RESET_FILTERS: {
      const { userQuery, type } = state;
      return {
        ...cloneDeep(defaultState),
        userQuery,
        type,
      };
    }

    case actionTypes.RESET_FROM_QUERY_PASTE: {
      const { query } = action.payload;
      return {
        ...state,
        userQuery: query,
        shouldResetParams: true,
      };
    }

    case actionTypes.SHOW_ALL_RESULTS: {
      const { hitsDisplayedCount } = action.payload;
      return {
        ...state,
        from: hitsDisplayedCount,
        onlyTopResults: false,
      };
    }

    case actionTypes.SELECT_KEYWORD: {
      const { keyword } = action.payload;

      return {
        ...state,
        ...getFreshQueryParams(),
        selectedKeywords: state.selectedKeywords.concat(keyword),
      };
    }

    case actionTypes.UNSELECT_KEYWORD: {
      const { keyword } = action.payload;

      return {
        ...state,
        ...getFreshQueryParams(),
        selectedKeywords: state.selectedKeywords.filter(
          (word) => word !== keyword,
        ),
      };
    }

    case actionTypes.ADD_RESTRICTION_TO_LAW_ARTICLE_IDS: {
      const { lawArticleIds } = action.payload;

      return {
        ...state,
        ...getFreshQueryParams(),
        restrictToLawArticleIds: lawArticleIds,
      };
    }

    case actionTypes.ADD_RESTRICTION_TO_LAW_ARTICLE_ID: {
      const { lawArticleId } = action.payload;

      return {
        ...state,
        ...getFreshQueryParams(),
        restrictToLawArticleIds: state.restrictToLawArticleIds.concat(
          lawArticleId,
        ),
      };
    }

    case actionTypes.REMOVE_RESTRICTION_TO_LAW_ARTICLE_ID: {
      const { lawArticleId } = action.payload;

      return {
        ...state,
        ...getFreshQueryParams(),
        restrictToLawArticleIds: state.restrictToLawArticleIds.filter(
          (word) => word !== lawArticleId,
        ),
      };
    }

    case actionTypes.CLEAR_QUERY_PARAMS: {
      return cloneDeep(defaultState);
    }

    case actionTypes.SYNC_URL_PARAMS: {
      const { params } = action.payload;
      return merge(cloneDeep(defaultState), params);
    }

    case actionTypes.RESTRICT_TO_LAWYER: {
      const { lawyerId, lawyerName, juridiction, ville } = action.payload;
      const newState = cloneDeep(defaultState);
      newState.lid = lawyerId;
      newState.userQuery = lawyerName;

      if (juridiction) {
        newState.aggregationsPaths = [
          `juridiction${aggregationSeparator}${juridiction}`,
        ];
      }
      if (ville) {
        newState.aggregationsPaths = [`ville${aggregationSeparator}${ville}`];
      }
      return newState;
    }

    case actionTypes.RESTRICT_TO_ENTERPRISE: {
      const {
        enterpriseId,
        raisonSociale,
        juridiction,
        ville,
        year,
      } = action.payload;
      const newState = cloneDeep(defaultState);
      newState.eid = enterpriseId;
      newState.userQuery = raisonSociale;

      if (juridiction) {
        newState.aggregationsPaths = [
          `juridiction${aggregationSeparator}${juridiction}`,
        ];
      }
      if (ville) {
        newState.aggregationsPaths = [`ville${aggregationSeparator}${ville}`];
      }
      if (year) {
        newState.userQuery += ` en ${year}`;
      }
      return newState;
    }

    case actionTypes.MANUAL_SUBMIT_SEARCH: {
      return {
        ...state,
        ...getFreshQueryParams(),
      };
    }

    default: {
      return state;
    }
  }
}
